import { ReactNode, useEffect } from 'react';
import clsx from 'clsx';
import { useFileUploadReducer } from 'components/Signup/hooks/useFileUploadReducer';
import { useStateKey } from 'components/Signup/hooks/useStateKey';
import {
  TDocumentType,
  TOfferDataRequirement,
} from 'lendingApi/lapiOverrides.types';
import { useGetDocuments } from 'lendingApi/lendingApiHooks';
import { UploadedFiles } from '../../../UploadedFiles/UploadedFiles';
import { UploadButton } from '../UploadButton/UploadButton';

import styles from './DocumentRequirement.module.css';
import { Required } from './Required';

type TProps = {
  isOptional?: boolean;
  title?: string | ReactNode;
  children: JSX.Element;
  className?: string;
  documentType?: TDocumentType;
  status?: TOfferDataRequirement['actions'][number]['status'];
  customButton?: React.ReactNode;
  isSubmitted?: boolean;
};

export const DocumentRequirement = ({
  isOptional,
  title,
  children,
  className,
  documentType,
  customButton,
  status,
  isSubmitted,
}: TProps) => {
  const stateKey = useStateKey();
  const { data: documentsData } = useGetDocuments(stateKey);

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  useEffect(() => {
    if (documentsData) {
      const documentsForThisDocumentType = documentsData.data.filter(
        (document) => document.attributes.document_type === documentType,
      );
      setAllFiles(documentsForThisDocumentType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, documentsData]);

  const renderRequiredError = () => {
    if (
      isSubmitted &&
      !isOptional &&
      (status === 'required' || status === 'error')
    ) {
      return <Required />;
    }
  };

  return (
    <div
      id={`document-requirement-${documentType}`}
      className={clsx(
        {
          [styles.optionalDocumentContainer]: isOptional,
          [styles.requiredDocumentContainer]: !isOptional,
        },
        className,
      )}
    >
      <div className={styles.documentRequirement}>
        <div className={styles.descriptionContainer}>
          {title && (
            <h3
              className={
                isOptional ? styles.optionalTitle : styles.requiredTitle
              }
            >
              {title}
              {renderRequiredError()}
            </h3>
          )}
          <div className={styles.description}>{children}</div>
          {uploadedFiles && (
            <UploadedFiles files={uploadedFiles} removeFile={removeFile} />
          )}
        </div>
        <div className={styles.buttonContainer}>
          {customButton ? (
            customButton
          ) : (
            <UploadButton
              isOptional={isOptional}
              documentType={documentType}
              setFileLoading={setFileLoading}
              setFileSuccess={setFileSuccess}
              setFileError={setFileError}
              setFileReject={setFileReject}
            />
          )}
        </div>
      </div>
    </div>
  );
};
